import { feeCalculator, getMerchantRateCards } from '@tabeo/finance'
import moment from 'moment'
import { isSDCMerchant } from './merchant'

export const SENT_STATUSES = ['new']
export const OPENED_STATUSES = ['reviewed']
export const APPLYING_FOR_LOAN_STATUSES = [
  'payment_plan_created',
  'consumer_details_created',
  'patient_details_created',
  'consumer_employment_details_created',
  'consumer_living_details_created',
  'risk_file_created',
  'consumer_personal_details_update_required',
  'consumer_personal_details_updated',
  'risk_check_pending',
  'payment_plan_resolved',
  'partner_income_verification_pending',
]

export const MANUAL_REVIEW_REQUIRED_STATUSES = ['manual_review_required']

export const APPLYING_WITH_IFINANCE_STATUSES = ['ifinance_review_pending']

export const LOAN_OFFER_MADE_STATUSES = [
  'pay_over_time_product_file_created',
  'manual_review_omitted',
  'manual_review_passed',
  'consumer_id_questions_omitted',
  'consumer_id_questions_created',
  'consumer_id_questions_passed',
  'payment_plan_scheduled',
  'payment_source_created',
  'bank_account_created',
  'first_payment_authorized',
  'loan_agreement_created',
  'ifinance_approved',
]
export const UNSUCCESSFUL_STATUSES = [
  'risk_checks_failed',
  'manual_review_failed',
]
export const CARD_DECLINED_STATUSES = [
  'consumer_id_questions_failed',
  'payment_source_failed',
  'bank_account_failed',
  'first_payment_authorization_failed',
  'pay_now_payment_authorization_failed',
]
export const PENDING_RELEASE_STATUSES = [
  'pay_now_payment_authorization_succeeded',
  'loan_agreement_accepted',
  'confirmed',
]
export const FIRST_PAYMENT_FAILED_STATUSES = ['first_payment_capture_failed']
export const LOAN_STARTED_STATUSES = ['first_payment_captured', 'subscribed']
export const PAYOUT_SCHEDULED_STATUSES = ['payout_scheduled']
export const PAYOUT_CANCELED_STATUSES = [
  'initial_partial_merchant_proceeds_transfer_cancelled',
  'merchant_proceeds_transfer_cancelled',
]
export const FUNDS_ON_THE_WAY_STATUSES = [
  'pay_now_payment_succeeded',
  'merchant_proceeds_transferred',
  'subscription_failed',
]
export const SETTLED_STATUSES = [
  'merchant_payout_processed',
  'final_partial_merchant_proceeds_transferred',
]
export const FULFILLMENT_REQUIRED_STATUSES = ['fulfillment_required']
export const EXPIRED_STATUSES = ['expired']
export const CANCELED_STATUSES = ['canceled']

const getState = ({
  status,
  paymentPlan,
  isTabeoPlus,
  isIFinance,
  merchant,
  paymentSourceID,
  isPOT,
  preCheck,
  payoutFrozen,
}) => {
  if (SENT_STATUSES.includes(status) && preCheck) {
    return 'pre_check_sent'
  }
  if (OPENED_STATUSES.includes(status) && preCheck) {
    return 'pre_check_opened'
  }
  if (APPLYING_FOR_LOAN_STATUSES.includes(status) && preCheck) {
    return 'pre_check_applying'
  }
  if (
    (UNSUCCESSFUL_STATUSES.includes(status) ||
      CARD_DECLINED_STATUSES.includes(status)) &&
    preCheck
  ) {
    return 'pre_check_application_failed'
  }
  if (
    (MANUAL_REVIEW_REQUIRED_STATUSES.includes(status) ||
      LOAN_OFFER_MADE_STATUSES.includes(status)) &&
    preCheck
  ) {
    return 'pre_check_approved'
  }

  const isMerchantConfirmationFlow =
    merchant?.loanConfirmationMode === 'merchant_confirmation'
  if (SENT_STATUSES.includes(status)) {
    return 'sent'
  }
  if (OPENED_STATUSES.includes(status)) {
    return 'opened'
  }
  if (APPLYING_FOR_LOAN_STATUSES.includes(status)) {
    return 'applying'
  }
  if (
    MANUAL_REVIEW_REQUIRED_STATUSES.includes(status) ||
    (isIFinance && status === 'manual_review_passed')
  ) {
    return 'manual_review_required'
  }

  if (
    LOAN_OFFER_MADE_STATUSES.includes(status) ||
    // Direct debit + merchant confirmation flow
    // consumer is prompted to add card details after loan agreement
    (status === 'loan_agreement_accepted' &&
      !paymentSourceID &&
      isMerchantConfirmationFlow)
  ) {
    return 'offer_made'
  }
  if (UNSUCCESSFUL_STATUSES.includes(status)) {
    return 'unsuccessful'
  }
  if (CARD_DECLINED_STATUSES.includes(status)) {
    return 'card_declined'
  }
  if (PENDING_RELEASE_STATUSES.includes(status)) {
    return 'funds_pending_release'
  }
  if (FIRST_PAYMENT_FAILED_STATUSES.includes(status)) {
    return 'first_payment_failed'
  }
  if (
    isPOT &&
    (LOAN_STARTED_STATUSES.includes(status) ||
      (FUNDS_ON_THE_WAY_STATUSES.includes(status) &&
        paymentPlan?.lender?.payoutType === 'unfunded') ||
      (PAYOUT_SCHEDULED_STATUSES.includes(status) &&
        paymentPlan?.lender?.payoutType === 'unfunded'))
  ) {
    return 'loan_started'
  }
  // While the payout is frozen (unresolved payout_frozen ticket) the offer
  // keeps transitioning through its usual payout statuses, but the payout
  // itself is excluded from scheduling until the ticket is resolved.
  if (
    payoutFrozen &&
    (FUNDS_ON_THE_WAY_STATUSES.includes(status) ||
      PAYOUT_SCHEDULED_STATUSES.includes(status))
  ) {
    return 'payout_on_hold'
  }
  if (
    FUNDS_ON_THE_WAY_STATUSES.includes(status) ||
    (PAYOUT_SCHEDULED_STATUSES.includes(status) && isTabeoPlus)
  ) {
    return 'funds_on_the_way'
  }
  if (FULFILLMENT_REQUIRED_STATUSES.includes(status)) {
    return 'fulfillment_required'
  }
  if (PAYOUT_SCHEDULED_STATUSES.includes(status)) {
    return 'payout_scheduled'
  }
  if (PAYOUT_CANCELED_STATUSES.includes(status)) {
    return 'payout_canceled'
  }
  if (SETTLED_STATUSES.includes(status)) {
    return 'settled'
  }
  if (EXPIRED_STATUSES.includes(status)) {
    return 'expired'
  }
  if (CANCELED_STATUSES.includes(status)) {
    return 'canceled'
  }
  if (APPLYING_WITH_IFINANCE_STATUSES.includes(status)) {
    return 'applying_with_ifinance'
  }
  return status
}

const normalizeActivity = transaction => {
  const {
    stateMachineTransitions = [],
    createdAt,
    preCheck = false,
  } = transaction

  return [
    {
      to: 'new',
      at: createdAt,
      state: 'sent',
      uuid: '234923840328',
    },
    ...stateMachineTransitions,
  ].map(i => ({
    ...i,
    state: getState({ ...transaction, status: i.status, preCheck }),
  }))
}

const normalizeChecks = baseChecks => {
  const checks = baseChecks.reduce((checks, check) => {
    const normalizedCheck = check

    if (check.name === 'application_payment_plan_applicability') {
      normalizedCheck.checks = [
        {
          ...check,
          title: 'Is payment plan possible',
          message:
            check.status === 'passed'
              ? 'Payment plan is possible'
              : 'Payment plan is not possible',
        },
      ]
    } else if (check.name === 'partner_income_questions') {
      normalizedCheck.checks = [
        {
          ...check,
          title: 'Is the required partner-income at or above the minimum?',
        },
      ]
    }

    // spaghetti bolognese, remove this when the backend has a better way of doing this
    normalizedCheck.checks.map(c => {
      if (
        [
          'onfido_document',
          'onfido_facial',
          'onfido_pep',
          'onfido_sanctions',
        ].includes(c.name) &&
        c.status === 'manual_review_required' &&
        !c.message.includes('failed')
      ) {
        c.status = 'pending'
      }

      return c
    })

    checks.push(normalizedCheck)
    return checks
  }, [])
  return checks
}

const normalize = transaction => {
  const paymentPlans = [...(transaction.paymentPlans || [])]
    .sort((a, b) => a.id - b.id)
    .map(plan => ({
      ...plan,
      payments: [...(plan.payments || [])].sort((a, b) => a.id - b.id),
    }))

  const paymentPlan = paymentPlans?.find(p => p.status !== 'canceled')
  const activity = normalizeActivity(transaction)

  const defaultRiskProfile = transaction.metadata?.riskProfiles?.find(
    p => !!p.loanOptions.find(o => o.isAvailableForInitialPaymentPlan)
  )
  const defaultRiskProfiles = transaction.metadata?.riskProfiles?.filter(
    p => !!p.loanOptions.find(o => o.isAvailableForInitialPaymentPlan)
  )
  const isTabeoPlus =
    (paymentPlan?.riskProfileName || defaultRiskProfile?.name) === 'tabeo_plus'
  const isIFinance =
    (paymentPlan?.riskProfileName || defaultRiskProfile?.name) === 'nl_ifinance'

  const state = getState({
    ...transaction,
    paymentPlan,
    isTabeoPlus,
    isIFinance,
  })

  const checksPerRiskProfile =
    [...(transaction.checks || [])]
      .sort((a, b) => a.id - b.id)
      .reduce((mem, item) => {
        if (!mem[item.riskProfileName]) {
          mem[item.riskProfileName] = []
        }
        mem[item.riskProfileName].push(item)
        return mem
      }, {}) || {}

  const checks = Object.keys(checksPerRiskProfile).map(key => {
    return { profile: key, checks: normalizeChecks(checksPerRiskProfile[key]) }
  })

  const name = paymentPlan?.name

  const isConsumerConfirmationFlow =
    transaction?.merchant?.loanConfirmationMode === 'consumer_confirmation'
  const isMerchantConfirmationFlow =
    transaction?.merchant?.loanConfirmationMode === 'merchant_confirmation'

  const pronouns = {
    consumerHeShe: transaction.consumerGender === 'm' ? 'he' : 'she',
    consumerHisHer: transaction.consumerGender === 'm' ? 'his' : 'her',
  }

  const isPOT = transaction.type === 'pay_over_time'
  const isPN = transaction.type === 'pay_now'

  const confirmedEvent = (transaction.stateMachineTransitions || []).find(i =>
    ['first_payment_captured', 'pay_now_payment_succeeded'].includes(i.to)
  )
  const confirmedDate = confirmedEvent && confirmedEvent.at

  const { is_cooling_off_period_waived: isCoolingOffPeriodWaived } =
    transaction?.flags || {}

  const coolingOffPeriodEnds =
    !isCoolingOffPeriodWaived && confirmedDate
      ? moment(confirmedDate).add(14, 'day')
      : null

  const isDirectDebit = transaction.paymentGateway === 'gocardless'

  const refundsAmount = paymentPlan?.payments?.reduce((mem, item) => {
    if (item.type === 'ref_mer') {
      mem += -item.originalPayInAmount
      mem += item.refundedFeeAmount
      mem += -item.lateRefundFeeAmount
    }
    return mem
  }, 0)

  const paymentPlanStats = paymentPlan?.payments?.reduce(
    (mem, item) => {
      if (['failed', 'part_paid'].includes(item.status)) {
        const overdueAmount = item.amount - item.paidAmount
        mem.overdueAmount += overdueAmount
        mem.overdueInvoices.push(item.number)
      }
      if (!['succeeded'].includes(item.status)) {
        const remainingAmount = item.amount - item.paidAmount
        mem.remainingAmount += remainingAmount
      }
      return mem
    },
    {
      overdueAmount: 0,
      overdueInvoices: [],
      remainingAmount: paymentPlan.payments.length ? 0 : transaction.price,
    }
  )

  if (
    !transaction.feeAmount &&
    paymentPlan?.riskProfileName === 'nl_ifinance' &&
    paymentPlan?.numberOfMonths === 24
  ) {
    const { feeAmount } = feeCalculator(
      getMerchantRateCards(transaction.merchant),
      'nl_ifinance',
      paymentPlan,
      transaction.price
    )
    transaction.feeAmount = feeAmount
  }

  const merchantNetProceeds =
    transaction.price - transaction.feeAmount + (refundsAmount || 0)
  const originalMerchantNetProceeds = transaction.price - transaction.feeAmount
  const revisedMerchantNetProceeds =
    transaction.revisedPrice -
    transaction.revisedRequiredMerchantFeeAmount -
    transaction.merchantLateRefundFeeAmountToDate

  const feePercent = transaction.feeAmount / transaction.price
  const revisedFeePercent =
    transaction.revisedRequiredMerchantFeeAmount / transaction.revisedPrice || 0

  const refunds = paymentPlan?.payments?.filter(p => p.type === 'ref_mer')

  const manualReviewRequirementsUploads =
    transaction.manualReviewRequirements?.reduce(
      (mem, item) => [...mem, ...item.manualReviewRequirementFiles],
      []
    ) ?? []

  const isTabeoCollect = [
    'profile_sdc_unfunded',
    'plusdent_unfunded',
    'diamondwhites_unfunded',
    'uk_pl_unfunded',
    'smilewhites_unfunded',
    'impress_unfunded',
    'vinci_unfunded',
    'nl_collect',
    'it_collect',
    'fr_collect',
    'fr_sdc_collect',
    'in_house',
  ].includes(paymentPlan?.riskProfileName || defaultRiskProfile?.name)

  const isTabeoCore = [
    'default',
    'pay_in_three',
    'it_core',
    'fr_core',
    'fr_sdc_core',
  ].includes(paymentPlan?.riskProfileName || defaultRiskProfile?.name)

  const isNearPrime = ['near_prime'].includes(
    paymentPlan?.riskProfileName || defaultRiskProfile?.name
  )
  const isDeferredPaymentPlan =
    (paymentPlan?.riskProfileName || defaultRiskProfile?.name) === 'nl_collect'

  const isMerchantPartLender =
    isTabeoCollect || isTabeoPlus || isDeferredPaymentPlan

  const sumOfMerchantPaidRefunds = refunds?.reduce((mem, item) => {
    return mem + item.merchantRefundPaymentAmount
  }, 0)

  const sumMerchantNetProceedsTransfers =
    transaction.merchantNetProceedsTransfers?.reduce((mem, item) => {
      return mem + item.amount
    }, 0)

  const isVoided = transaction?.loan?.status === 'voided'

  const isSentAtHidden =
    new Date(transaction.createdAt) < new Date('2022-02-01T00:00:00.000Z')

  const scheduledTasks = transaction.scheduledTasks?.map(s => ({
    ...s,
    isSentAtHidden,
  }))

  const documents = transaction.documents?.map(d => {
    // Last minute change requested by Paul to hide sent at for offers created before deployment
    d.isSentAtHidden = isSentAtHidden

    const documentNotifications = {
      secci: 'consumer_secci_message',
      adequate_explanation: 'consumer_adequate_explanation_message',
    }

    const notificationName = documentNotifications[d.type]

    if (!notificationName) {
      return d
    }

    const notification = scheduledTasks?.find(
      st => st.name === notificationName
    )

    if (!notification) {
      return d
    }

    // Cross link the document with the notification
    notification.document = d

    return {
      ...d,
      notification,
    }
  })

  return {
    ...transaction,
    activity,
    state,
    ...pronouns,
    checks,
    name,
    paymentPlan: {
      ...paymentPlan,
      ...paymentPlanStats,
    },
    confirmedAt: confirmedDate,
    paymentPlans,
    isPOT,
    isPN,
    isConsumerConfirmationFlow,
    isMerchantConfirmationFlow,
    isIFinance,
    isCoolingOffPeriodWaived,
    coolingOffPeriodEnds,
    isSDCMerchant: isSDCMerchant(transaction.merchant),
    isDirectDebit,
    refundsAmount,
    refunds,
    merchantNetProceeds,
    originalMerchantNetProceeds,
    revisedMerchantNetProceeds,
    feePercent,
    revisedFeePercent,
    manualReviewRequirementsUploads,
    defaultRiskProfile,
    defaultRiskProfiles,
    isMerchantPartLender,
    isTabeoCore,
    isTabeoCollect,
    isTabeoPlus,
    isNearPrime,
    sumOfMerchantPaidRefunds,
    sumMerchantNetProceedsTransfers,
    isVoided,
    documents,
    scheduledTasks,
  }
}

export default normalize
